'use client'

import Image from 'next/image'
import { memo, useMemo } from 'react'
import sanitizeHtml from 'sanitize-html'
import { AddToCartButton } from '@/AddToCartButton'
import { Price } from '@/General/Price'
import {
  toProductDisplayOptions,
  type ProductDisplayOption,
} from '../../app/product/[productSlug]/helpers/productOptions'
import type { RegionalBrandCode } from '@/lib/seo/marketUrls'

export type ChildProductRow = {
  id: number
  name?: string | null
  urlStub?: string | null
  url?: string | null
  description?: string | null
  thumbnailUrl?: string | null
  displayPrice?: number | null
  isPreorder?: boolean | null
  options: Array<{
    price: number
    salePrice?: number | null
    priceIncVat?: number | null
    salePriceIncVat?: number | null
    taxType?: number | null
    inStock: boolean
    viPoints: number
    maxQuantity?: number | null
    description?: string | null
    id?: number | null
    needsShipping: boolean
    currency?: { symbol?: string | null; code?: string | null } | null
    productType?: { name?: string | null } | null
  }>
}

const ChildProductRowBlock = memo(function ChildProductRowBlock({
  row,
  parentHasDownloads,
  brandCode,
}: {
  row: ChildProductRow
  parentHasDownloads: boolean
  brandCode?: RegionalBrandCode | null
}) {
  const displayOptions: ProductDisplayOption[] = useMemo(
    () => toProductDisplayOptions(row.options, null),
    [row.options]
  )
  const firstOpt = row.options[0]
  const listPrice = row.displayPrice ?? firstOpt?.price ?? null

  return (
    <li
      id={`individual-${row.id}`}
      className="flex flex-col sm:flex-row gap-4 sm:gap-5 border-b border-gray-100 pb-10 last:border-0 last:pb-0"
    >
      {row.thumbnailUrl ? (
        <div className="flex-shrink-0 w-full sm:w-24">
          <Image
            src={row.thumbnailUrl}
            alt={row.name ?? 'Product image'}
            width={96}
            height={96}
            className="w-20 sm:w-24 max-w-full h-auto object-contain rounded border border-gray-100 bg-white"
            unoptimized
          />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <h3 className="text-xl md:text-2xl font-semibold text-gray-900 leading-snug">
          {row.name}
        </h3>
        {(listPrice != null && !Number.isNaN(Number(listPrice))) ||
        row.options.length > 0 ? (
          <div className="mt-1.5">
            <Price
              price={listPrice}
              salePrice={firstOpt?.salePrice ?? null}
              priceIncVat={firstOpt?.priceIncVat ?? null}
              salePriceIncVat={firstOpt?.salePriceIncVat ?? null}
              currencySymbol={firstOpt?.currency?.symbol ?? '$'}
              currencyCode={firstOpt?.currency?.code ?? null}
              taxType={firstOpt?.taxType ?? null}
              size="lg"
              options={row.options.length > 0 ? row.options : undefined}
              brandCode={brandCode}
            />
          </div>
        ) : null}
        {row.description ? (
          <div
            className="prose prose-sm max-w-none mt-3 text-gray-600"
            dangerouslySetInnerHTML={{
              __html: sanitizeDescription(row.description),
            }}
          />
        ) : null}
        <div className="mt-4 max-w-md">
          <AddToCartButton
            productId={row.id}
            productName={row.name || 'Product'}
            options={displayOptions}
            showOptions={displayOptions.length > 1}
            showEmailNotification={true}
            hasDownloads={parentHasDownloads}
            independentOptionSelection
            className="w-full sm:w-auto"
            configureFlowLinkClassName="block w-full sm:inline-block sm:w-auto text-center"
            isPreorder={row.isPreorder === true}
            preorderReleaseDate={null}
            brandCode={brandCode}
          />
        </div>
      </div>
    </li>
  )
})

function sanitizeDescription(html: string) {
  return sanitizeHtml(html, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      img: ['src', 'alt', 'title', 'width', 'height'],
      a: ['href', 'name', 'target', 'rel'],
    },
  })
}

export const ChildProductsSection = ({
  childProducts,
  parentHasDownloads,
  brandCode = null,
}: {
  childProducts: ChildProductRow[]
  parentHasDownloads: boolean
  brandCode?: RegionalBrandCode | null
}) => {
  if (!childProducts.length) return null

  const heading = parentHasDownloads
    ? 'Also available individually as hard copy or downloads:'
    : 'Also available individually:'

  return (
    <section
      id="individual"
      className="max-w-(--breakpoint-xl) mx-auto mt-10 border-t border-gray-200 pt-10"
      aria-labelledby="individual-heading"
    >
      <a id="individual-anchor" className="scroll-mt-24" aria-hidden />
      <h2
        id="individual-heading"
        className="text-xl md:text-2xl font-semibold text-gray-900 mb-6"
      >
        {heading}
      </h2>
      <ul className="flex flex-col gap-10">
        {childProducts.map(row => (
          <ChildProductRowBlock
            key={row.id}
            row={row}
            parentHasDownloads={parentHasDownloads}
            brandCode={brandCode}
          />
        ))}
      </ul>
    </section>
  )
}
